/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Slider from 'rc-slider';
import format from 'format-number';
import uniq from 'lodash/uniq';
import find from 'lodash/find';
import Arrow from 'components/Icons/Arrow/Arrow';
import useSticky from 'utils/sticky';
import useEnhanced from 'utils/enhanced';
import 'rc-slider/assets/index.css';
import 'rc-tooltip/assets/bootstrap.css';
import './Filter.scss';

const createSliderWithTooltip = Slider.createSliderWithTooltip;
const Range = createSliderWithTooltip(Slider.Range);

/**
 * This is the Filter component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */

const Filter = forwardRef(
  (
    { open, area, type, employees, rental, labels, onOpen, onChange, onReset },
    ref
  ) => {
    const enhanced = useEnhanced(1024);
    const node = useRef(null);
    const areaNode = useRef(null);
    const content = useRef(null);
    const toggle = useRef(null);
    const [opened, setOpened] = useState(open);
    const sticky = useSticky(node, enhanced, { offset: 40 });

    useEffect(() => {
      if (open) {
        document.body.classList.add('properties-filter-open');
      } else {
        setOpened(false);
      }

      return function cleanup() {
        document.body.classList.remove('properties-filter-open');
      };
    }, [open]);

    useImperativeHandle(ref, () => ({
      focus: () => {
        if (areaNode.current) {
          areaNode.current.focus();
        }
      },
    }));

    function renderEmployees() {
      const formatMin = format(employees.format.min);
      const formatMax = format(employees.format.max);
      const formatTip = format(employees.format.tip);

      return (
        <div className="field-row type-range employees">
          <label htmlFor={employees.id}>{employees.label}</label>
          <div className="marks">
            <div>{formatMin(employees.range.min)}</div>
            <div>{formatMax(employees.range.max)}</div>
          </div>
          <Range
            id={employees.id}
            trackStyle={{ height: '10px' }}
            {...employees.range}
            tipFormatter={value => {
              if (value === employees.range.max) {
                return formatMax(value);
              }
              return formatTip(value);
            }}
            value={employees.value}
            onChange={value => {
              onChange({
                [employees.id]: value,
              });
            }}
          />
        </div>
      );
    }

    function renderRental() {
      const formatMin = format(rental.format.min);
      const formatMax = format(rental.format.max);
      const formatTip = format(rental.format.tip);
      return (
        <div className="field-row type-range rental">
          <label
            htmlFor={rental.id}
            dangerouslySetInnerHTML={{ __html: rental.label }}
          />
          <div className="marks">
            <div>{formatMin(rental.range.min)}</div>
            <div>{formatMax(rental.range.max)}</div>
          </div>
          <Range
            {...rental.range}
            tipFormatter={value => {
              if (value === rental.range.max) {
                return formatMax(value);
              }
              return formatTip(value);
            }}
            value={rental.value}
            onChange={value => {
              onChange({
                [rental.id]: value,
              });
            }}
          />
        </div>
      );
    }

    return (
      <div
        ref={node}
        className={classnames('filter', 'scrolled', sticky, { open, opened })}
        onTransitionEnd={() => {
          setOpened(open);
        }}
      >
        <button
          ref={toggle}
          className="toggle"
          onClick={() => {
            onOpen(!open);
          }}
        >
          <Arrow right={!open} left={open} />
          <span className="text">{open ? labels.close : labels.open}</span>
        </button>
        <div ref={content} className="content">
          <button
            className="close"
            onClick={() => {
              onOpen(false);
            }}
          >
            <span>
              <Arrow left />
              <span className="text">{labels.close}</span>
            </span>
          </button>
          <div className="field-row type-select">
            <label htmlFor={area.id}>{area.label}</label>
            <select
              ref={areaNode}
              className="maroon"
              title={area.labels.hint}
              id={area.id}
              name={area.id}
              value=""
              onChange={({ target: { value } }) => {
                if (value.length) {
                  const selected = uniq([...area.value, parseInt(value)]);
                  onChange({
                    [area.id]: selected,
                  });
                } else {
                  onChange({
                    [area.id]: [],
                  });
                }
              }}
            >
              <option value="">{area.labels.all}</option>
              {area.options.map(({ id, title }) => (
                <option key={id} value={id}>
                  {title}
                </option>
              ))}
            </select>
            <ul>
              {area.value
                .map(id => find(area.options, { id }))
                .filter(value => value)
                .map(({ id, title }) => (
                  <li key={id}>
                    <button
                      title={area.labels.remove}
                      onClick={() => {
                        const selected = [...area.value].filter(
                          value => value !== id
                        );
                        onChange({
                          [area.id]: selected,
                        });
                      }}
                    >
                      <span>{title}</span>
                    </button>
                  </li>
                ))}
            </ul>
          </div>
          <div className="field-row type-select">
            <label htmlFor={type.id}>{type.label}</label>
            <select
              className="maroon"
              id={type.id}
              name={type.id}
              value={type.value}
              onChange={({ target: { value } }) => {
                onChange({
                  [type.id]: value,
                });
              }}
            >
              <option value="">{type.labels.all}</option>
              {type.options.map(({ id, title }) => (
                <option key={id} value={id}>
                  {title}
                </option>
              ))}
            </select>
          </div>
          {renderEmployees()}
          {renderRental()}
          <div className="buttons">
            <button
              className="button mud reset"
              onClick={() => {
                if (toggle.current) {
                  toggle.current.focus();
                }
                onReset();
              }}
            >
              <span className="text">{labels.reset}</span>
            </button>
            <button
              className="button orange submit"
              onClick={() => {
                onOpen(false);
              }}
            >
              <span className="text">{labels.submit}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }
);

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Filter.propTypes = {
  open: PropTypes.bool,
  area: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.array.isRequired,
    options: PropTypes.array.isRequired,
    labels: PropTypes.shape({
      hint: PropTypes.string.isRequired,
      all: PropTypes.string.isRequired,
      remove: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  type: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    options: PropTypes.array.isRequired,
    labels: PropTypes.shape({
      all: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  employees: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    range: PropTypes.shape({
      min: PropTypes.number.isRequired,
      max: PropTypes.number.isRequired,
    }).isRequired,
    format: PropTypes.shape({
      min: PropTypes.object.isRequired,
      max: PropTypes.object.isRequired,
      tip: PropTypes.object.isRequired,
    }).isRequired,
    value: PropTypes.array.isRequired,
  }).isRequired,
  rental: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    range: PropTypes.shape({
      min: PropTypes.number.isRequired,
      max: PropTypes.number.isRequired,
    }).isRequired,
    format: PropTypes.shape({
      min: PropTypes.object.isRequired,
      max: PropTypes.object.isRequired,
      tip: PropTypes.object.isRequired,
    }).isRequired,
    value: PropTypes.array.isRequired,
  }).isRequired,
  labels: PropTypes.shape({
    all: PropTypes.string.isRequired,
    open: PropTypes.string.isRequired,
    close: PropTypes.string.isRequired,
    submit: PropTypes.string.isRequired,
    reset: PropTypes.string.isRequired,
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Filter.defaultProps = {
  open: false,
  area: {
    id: 'area',
    label: 'Area',
    value: [],
    options: [],
    labels: {
      hint: 'Select to add area to filter',
      all: 'Add area',
      remove: 'Click to remove area',
    },
  },
  type: {
    id: 'type',
    label: 'Type',
    value: '',
    options: [],
    labels: {
      all: 'Select type',
    },
  },
  employees: {
    id: 'employees',
    label: 'Employees',
    range: {
      min: 0,
      max: 0,
    },
    format: {
      min: {
        suffix: '',
      },
      max: {
        suffix: ' +',
      },
      tip: {},
    },
    value: [0, 0],
  },
  rental: {
    id: 'rental',
    label: 'Rental',
    range: {
      min: 0,
      max: 0,
    },
    format: {
      min: {
        suffix: ',-',
      },
      max: {
        suffix: ',-+',
      },
      tip: {
        suffix: ',-',
      },
    },
    value: [0, 0],
  },
  labels: {
    all: 'All',
    open: 'Open filter',
    close: 'Close filter',
    submit: 'Submit',
    reset: 'Reset',
  },
  onOpen: () => {},
  onChange: () => {},
  onReset: () => {},
};

export default Filter;

// export default forwardRef((props, ref) => <Filter innerRef={ref} {...props} />);
